import type { Table } from '@tanstack/react-table'
import { SegmentedControl, type SegmentedOption } from '@components/atoms/SegmentedControl'
import { ColumnVisibilityDropdown } from '@molecules/ColumnVisibilityDropdown'
import { scopedTranslation } from '@frontend/utils/I18n'
import { SupporterSearchField } from '../search-supporters/SupporterSearchField'
import SortSupporters from '../sort-supporters/SortSupporters'
import FilterDialog from '../filter-supporters/FilterDialog'
import ActiveFiltersBar from '../filter-supporters/ActiveFiltersBar'

const t = scopedTranslation('group_show.view_mode')

export type ViewMode = 'card' | 'table'

type SupporterToolbarProps<TData> = {
  viewMode: ViewMode
  onViewModeChange: (mode: ViewMode) => void
  table: Table<TData>
  groupId: string
  canExportGroupSupporters: boolean
  matchingCount: number
  totalCount: number
}

export function SupporterToolbar<TData>({
  viewMode,
  onViewModeChange,
  table,
  groupId,
  canExportGroupSupporters,
  matchingCount,
  totalCount,
}: SupporterToolbarProps<TData>) {
  const viewModeOptions: SegmentedOption<ViewMode>[] = [
    { value: 'card', label: t('card'), leadingIcon: 'users-01' },
    { value: 'table', label: t('table'), leadingIcon: 'dot-points' },
  ]

  return (
    <div>
      <div className="tw:flex tw:flex-wrap tw:items-end tw:gap-4 tw:pb-6">
        <div className="tw:min-w-[16rem] tw:flex-1">
          <SupporterSearchField />
        </div>
        <div className="tw:w-max tw:shrink-0">
          <SortSupporters />
        </div>
        <FilterDialog />
        <SegmentedControl<ViewMode>
          options={viewModeOptions}
          value={viewMode}
          onChange={onViewModeChange}
          ariaLabel={t('group_label')}
        />
        {viewMode === 'table' && (
          <div className="tw:hidden tw:md:block">
            <ColumnVisibilityDropdown
              table={table}
              nonHideableColumns={['name', 'actions']}
            />
          </div>
        )}
      </div>
      <div>
        <ActiveFiltersBar
          groupId={groupId}
          canExportGroupSupporters={canExportGroupSupporters}
          matchingCount={matchingCount}
          totalCount={totalCount}
        />
      </div>
    </div>
  )
}
